/**
 * V2 rebuild against Figma frame a_start_profile_setup_2 (393×862).
 * Functional contract preserved: route branching via runCode, post-login
 * redirect via state, ?join=<code> forwarding for Login wizard chrome.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import OnboardingShellV2 from '../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../components/ProgressIndicatorV2';
import heroProfile from '../assets/onboarding/hero-profile.svg';
import { pillButtonFocusRing } from '../styles/onboardingButtons';
import { useWizardState } from '../wizard/useWizardState';
import { getStepInfo } from '../wizard/wizardSteps';
import { getNextAfterAuth } from '../routes/ProfileSetupRedirect';

const EntryChoiceStep: React.FC = () => {
  const { t } = useTranslation('entryChoice');
  const navigate = useNavigate();
  const location = useLocation();
  const { code: routeCode } = useParams<{ code?: string }>();
  const wizard = useWizardState();
  const step = getStepInfo('entry-choice', wizard.pathKind);

  // Direct-URL fix — when the user lands on /join/:code/auth-choice without
  // first passing through /join/:code/ (Welcome), the wizard's runCode is
  // null. Pull it from the route params so Create/Login still route into
  // the join flow rather than falling back to /register.
  useEffect(() => {
    if (routeCode && wizard.pathKind !== 'with-code') {
      wizard.setPathKind('with-code');
    }
    if (routeCode && !wizard.runCode) {
      wizard.setRunCode(routeCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCode]);

  const effectiveRunCode = routeCode ?? wizard.runCode;

  const goCreate = () => {
    const target = effectiveRunCode
      ? `/join/${effectiveRunCode}/profile`
      : '/register';
    navigate({ pathname: target, search: location.search });
  };

  const goLogin = () => {
    const postLogin = getNextAfterAuth(effectiveRunCode ?? undefined);
    const search = new URLSearchParams(location.search);
    if (effectiveRunCode) search.set('join', effectiveRunCode);
    const qs = search.toString();
    navigate(
      { pathname: '/login', search: qs ? `?${qs}` : '' },
      { state: { from: { pathname: postLogin } } },
    );
  };

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          paddingBlockStart: '57px',
          gap: '24px',
        }}
      >
        <Box
          component="img"
          src={heroProfile}
          alt=""
          sx={{
            width: '100px',
            height: '95px',
            alignSelf: 'center',
            display: 'block',
            objectFit: 'contain',
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: 0,
            color: '#322F35',
            margin: 0,
            marginBlockStart: '-2px',
          }}
        >
          {t('headline')}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '0.15px',
            color: '#605D64',
            marginBlockStart: '-3px',
          }}
        >
          {t('subtitle', { defaultValue: 'Select your preferred way to start:' })}
        </Typography>
        {/*
          Frame 40 (buttons column): itemSpacing=8 between two 48-px-tall
          state-layer bands. Each band wraps a 40-px visible pill vertically
          centered (4 px top + 40 px pill + 4 px bottom). Without these outer
          48-px wrappers, the pill-to-pill stride collapses from Figma's
          56 px (4+40+8+4) to 48 px (40+8), placing the outline button 8 px
          too high. The wrappers also shift the blue pill down 4 px so its
          visible top lands at Figma y=280 instead of y=276 (the band top).
          See SKILL.md edge case "M3 state-layer band — 48 px wrapper around
          40 px content".
        */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
          <Box
            sx={{
              minHeight: '48px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={goCreate}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                cursor: 'pointer',
                minWidth: '162px',
                minHeight: '40px',
                paddingInline: '16px',
                borderRadius: '100px',
                backgroundColor: '#4D69B9',
                color: '#FFFFFF',
                fontFamily: "'Roboto', sans-serif",
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.1px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: '#3D54A0' },
                ...pillButtonFocusRing,
              }}
            >
              {t('createAccount')}
            </Box>
          </Box>
          <Box
            sx={{
              minHeight: '48px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={goLogin}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                cursor: 'pointer',
                minWidth: '140px',
                minHeight: '40px',
                paddingInline: '16px',
                borderRadius: '100px',
                // Figma stroke=#CAC4D0@1.0 align=INSIDE — inset box-shadow matches
                // Figma's INSIDE-stroke semantics without inflating the layout
                // box (CSS `border` adds outside the content+padding box and
                // would push the pill to 42 px tall). See SKILL.md edge case
                // "strokeAlign: INSIDE inflates content-sized boxes by 2 px".
                boxShadow: 'inset 0 0 0 1px var(--color-outline-variant, #CAC4D0)',
                color: 'text.secondary',
                fontFamily: "'Roboto', sans-serif",
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.1px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
                ...pillButtonFocusRing,
              }}
            >
              {t('loginExisting')}
            </Box>
          </Box>
        </Box>
      </Box>
    </OnboardingShellV2>
  );
};

export default EntryChoiceStep;
